import React from 'react';
import BusinessLegalSummary from '../components/layout/BusinessLegalSummary';
import { Shield, Lock, Eye, FileText, Mail } from 'lucide-react';

const Privacy = () => {
  return (
    <div className="min-h-screen bg-gray-50 py-12">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center mb-12">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-orange-100 rounded-full mb-4">
            <Shield className="h-8 w-8 text-orange-600" />
          </div>
          <h1 className="text-4xl font-bold text-gray-900 mb-4">Gizlilik Politikası</h1>
          <p className="text-gray-600">
            Son güncelleme: {new Date().toLocaleDateString('tr-TR', { year: 'numeric', month: 'long', day: 'numeric' })}
          </p>
        </div>

        {/* Content */}
        <div className="bg-white rounded-lg shadow-lg p-8 md:p-12 space-y-8">
          
          {/* Introduction */}
          <section>
            <h2 className="text-2xl font-semibold text-gray-900 mb-4 flex items-center">
              <FileText className="h-6 w-6 text-orange-600 mr-2" />
              1. Giriş
            </h2>
            <p className="text-gray-700 leading-relaxed mb-4">
              LeaserTech ("biz", "bizim" veya "firmamız") olarak, ziyaretçilerimizin ve müşterilerimizin gizliliğine büyük önem veriyoruz. 
              Bu Gizlilik Politikası, web sitemizi kullandığınızda veya hizmetlerimizi satın aldığınızda topladığımız bilgileri, 
              bu bilgileri nasıl kullandığımızı ve koruduğumuzu açıklamaktadır.
            </p>
            <p className="text-gray-700 leading-relaxed">
              Bu politikayı kabul ederek, bilgilerinizin bu politikada belirtilen şekilde işlenmesine izin vermiş olursunuz.
            </p>
          </section>

          {/* Data Collection */}
          <section>
            <h2 className="text-2xl font-semibold text-gray-900 mb-4 flex items-center">
              <Eye className="h-6 w-6 text-orange-600 mr-2" />
              2. Toplanan Kişisel Veriler
            </h2>
            <p className="text-gray-700 leading-relaxed mb-4">
              Sizden topladığımız bilgiler şunları içerebilir:
            </p>
            <ul className="list-disc list-inside space-y-2 text-gray-700 ml-4">
              <li>
                <strong>Kimlik Bilgileri:</strong> Ad, soyad, e-posta adresi, telefon numarası, fatura ve teslimat adresi
              </li>
              <li>
                <strong>Ödeme Bilgileri:</strong> Kredi kartı bilgileri (ödeme işlemi güvenli ödeme sağlayıcısı aracılığıyla işlenir), 
                fatura bilgileri
              </li>
              <li>
                <strong>Sipariş Bilgileri:</strong> Sipariş geçmişi, satın alınan ürünler, sipariş tutarları, teslimat durumu
              </li>
              <li>
                <strong>Hesap Bilgileri:</strong> Kullanıcı adı, şifre (şifrelenmiş olarak saklanır), hesap tercihleri, 
                favori ürünler
              </li>
              <li>
                <strong>Teknik Bilgiler:</strong> IP adresi, tarayıcı türü ve versiyonu, işletim sistemi, cihaz bilgileri, 
                ziyaret edilen sayfalar, ziyaret süresi
              </li>
              <li>
                <strong>İletişim Bilgileri:</strong> Müşteri hizmetleri ile yaptığınız görüşmeler, geri bildirimler, 
                şikayetler
              </li>
            </ul>
          </section>

          {/* Data Usage */}
          <section>
            <h2 className="text-2xl font-semibold text-gray-900 mb-4 flex items-center">
              <Lock className="h-6 w-6 text-orange-600 mr-2" />
              3. Kişisel Verilerin Kullanım Amaçları
            </h2>
            <p className="text-gray-700 leading-relaxed mb-4">
              Topladığımız kişisel verileri aşağıdaki amaçlar için kullanıyoruz:
            </p>
            <ul className="list-disc list-inside space-y-2 text-gray-700 ml-4">
              <li>Siparişlerinizi işlemek, teslimat yapmak ve sipariş durumunu takip etmek</li>
              <li>Hesabınızı oluşturmanızı ve yönetmenizi sağlamak</li>
              <li>Ödeme işlemlerini gerçekleştirmek ve doğrulamak</li>
              <li>Müşteri hizmetleri desteği sağlamak ve sorularınızı yanıtlamak</li>
              <li>Ürünlerimiz, hizmetlerimiz ve kampanyalarımız hakkında bilgilendirme yapmak</li>
              <li>Web sitemizi iyileştirmek, kişiselleştirmek ve geliştirmek</li>
              <li>Güvenlik, dolandırıcılık önleme ve yasal yükümlülükleri yerine getirmek</li>
              <li>İstatistiksel analizler ve pazar araştırmaları yapmak</li>
              <li>Yasal gereksinimlere uymak ve yasal haklarımızı korumak</li>
            </ul>
          </section>

          {/* Data Sharing */}
          <section>
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">
              4. Kişisel Verilerin Paylaşılması
            </h2>
            <p className="text-gray-700 leading-relaxed mb-4">
              Kişisel verilerinizi, aşağıdaki durumlar dışında üçüncü taraflarla paylaşmıyoruz:
            </p>
            <ul className="list-disc list-inside space-y-2 text-gray-700 ml-4">
              <li>
                <strong>Ödeme Sağlayıcıları:</strong> Ödeme işlemlerini gerçekleştirmek için güvenilir ödeme sağlayıcıları 
                (PaynKolay, Visa, MasterCard vb.) ile paylaşılır
              </li>
              <li>
                <strong>Kargo Firmaları:</strong> Siparişlerinizi teslim etmek için kargo ve lojistik firmaları ile 
                paylaşılır
              </li>
              <li>
                <strong>Hizmet Sağlayıcıları:</strong> Web sitemizin işletilmesi, e-posta gönderimi, analiz vb. için 
                hizmet sağlayıcılarımız ile paylaşılır
              </li>
              <li>
                <strong>Yasal Yükümlülükler:</strong> Kanun, düzenleme veya mahkeme kararı gereği paylaşılması zorunlu 
                olduğunda
              </li>
              <li>
                <strong>İş Ortakları:</strong> Onayınız dahilinde, iş ortaklarımız ile paylaşılabilir
              </li>
            </ul>
          </section>

          {/* Data Security */}
          <section>
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">
              5. Veri Güvenliği
            </h2>
            <p className="text-gray-700 leading-relaxed mb-4">
              Kişisel verilerinizin güvenliğini sağlamak için teknik ve idari önlemler alıyoruz:
            </p>
            <ul className="list-disc list-inside space-y-2 text-gray-700 ml-4">
              <li>SSL sertifikası ile şifrelenmiş veri iletimi</li>
              <li>Güvenli ödeme sistemleri kullanımı</li>
              <li>Şifrelerin hashlenmiş olarak saklanması</li>
              <li>Düzenli güvenlik denetimleri ve güncellemeler</li>
              <li>Sınırlı erişim kontrolleri ve yetkilendirme sistemleri</li>
              <li>Veri yedekleme ve felaket kurtarma planları</li>
            </ul>
          </section>

          {/* Cookies */}
          <section>
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">
              6. Çerezler (Cookies)
            </h2>
            <p className="text-gray-700 leading-relaxed mb-4">
              Web sitemizde deneyiminizi iyileştirmek için çerezler kullanıyoruz. Çerezler, web sitelerinin bilgisayarınıza 
              veya mobil cihazınıza kaydettiği küçük metin dosyalarıdır.
            </p>
            <p className="text-gray-700 leading-relaxed mb-4">
              Kullandığımız çerez türleri:
            </p>
            <ul className="list-disc list-inside space-y-2 text-gray-700 ml-4">
              <li><strong>Zorunlu Çerezler:</strong> Web sitesinin temel işlevlerini yerine getirmesi için gereklidir</li>
              <li><strong>Analitik Çerezler:</strong> Site kullanımını analiz etmek için kullanılır (Google Analytics)</li>
              <li><strong>Fonksiyonel Çerezler:</strong> Tercihlerinizi hatırlamak için kullanılır</li>
              <li><strong>Pazarlama Çerezleri:</strong> Size özel içerik ve reklamlar sunmak için kullanılır</li>
            </ul>
            <p className="text-gray-700 leading-relaxed mt-4">
              Tarayıcı ayarlarınızdan çerezleri yönetebilir veya devre dışı bırakabilirsiniz. Ancak bazı çerezleri devre 
              dışı bırakmak web sitesinin bazı özelliklerinin çalışmamasına neden olabilir.
            </p>
          </section>

          {/* User Rights */}
          <section>
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">
              7. Haklarınız (KVKK)
            </h2>
            <p className="text-gray-700 leading-relaxed mb-4">
              6698 sayılı Kişisel Verilerin Korunması Kanunu (KVKK) kapsamında aşağıdaki haklara sahipsiniz:
            </p>
            <ul className="list-disc list-inside space-y-2 text-gray-700 ml-4">
              <li>Kişisel verilerinizin işlenip işlenmediğini öğrenme</li>
              <li>İşlenmişse buna ilişkin bilgi talep etme</li>
              <li>İşlenme amacını ve amacına uygun kullanılıp kullanılmadığını öğrenme</li>
              <li>Yurt içinde veya yurt dışında aktarıldığı üçüncü kişileri bilme</li>
              <li>Eksik veya yanlış işlenmişse düzeltilmesini isteme</li>
              <li>İşlenmesini gerektiren sebeplerin ortadan kalkması halinde silinmesini veya yok edilmesini isteme</li>
              <li>Düzeltme, silme, yok etme işlemlerinin aktarıldığı üçüncü kişilere bildirilmesini isteme</li>
              <li>Münhasıran otomatik sistemler ile analiz edilmesi nedeniyle aleyhinize bir sonuç doğmasına itiraz etme</li>
              <li>Kanuna aykırı olarak işlenmesi sebebiyle zarara uğramanız hâlinde zararın giderilmesini talep etme</li>
            </ul>
            <p className="text-gray-700 leading-relaxed mt-4">
              Bu haklarınızı kullanmak için{' '}
              <a href="/contact" className="text-orange-600 hover:text-orange-700 underline">
                İletişim
              </a>{' '}
              sayfasından bizimle iletişime geçebilirsiniz.
            </p>
          </section>

          {/* Data Retention */}
          <section>
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">
              8. Veri Saklama Süresi
            </h2>
            <p className="text-gray-700 leading-relaxed">
              Kişisel verileriniz, toplama amaçlarımız için gerekli olduğu sürece ve yasal saklama yükümlülüklerimiz 
              çerçevesinde saklanır. Yasal saklama süreleri dolduktan veya verilerin işlenme amacı ortadan kalktıktan sonra, 
              verileriniz güvenli bir şekilde silinir veya anonimleştirilir.
            </p>
          </section>

          {/* Third Party Links */}
          <section>
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">
              9. Üçüncü Taraf Bağlantılar
            </h2>
            <p className="text-gray-700 leading-relaxed">
              Web sitemizde üçüncü taraf web sitelerine bağlantılar bulunabilir. Bu bağlantıların gizlilik uygulamalarından 
              sorumlu değiliz. Başka bir siteye gittiğinizde o sitenin gizlilik politikasını okumanızı öneririz.
            </p>
          </section>

          {/* Policy Changes */}
          <section>
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">
              10. Gizlilik Politikası Değişiklikleri
            </h2>
            <p className="text-gray-700 leading-relaxed">
              Bu Gizlilik Politikasını zaman zaman güncelleyebiliriz. Önemli değişiklikler yaptığımızda, web sitemizde 
              veya e-posta yoluyla sizi bilgilendireceğiz. Politikanın en güncel versiyonu her zaman bu sayfada yer alacaktır.
            </p>
          </section>

          {/* Contact */}
          <section className="bg-orange-50 border-l-4 border-orange-500 p-6 rounded-r-lg">
            <h2 className="text-2xl font-semibold text-gray-900 mb-4 flex items-center">
              <Mail className="h-6 w-6 text-orange-600 mr-2" />
              11. İletişim
            </h2>
            <p className="text-gray-700 leading-relaxed mb-4">
              Gizlilik politikamız hakkında sorularınız veya kişisel verilerinizle ilgili talepleriniz için bizimle iletişime geçebilirsiniz:
            </p>
            <BusinessLegalSummary className="text-gray-700" />
            <div className="space-y-2 text-gray-700 mt-4">
              <p className="mt-4">
                <a href="/contact" className="text-orange-600 hover:text-orange-700 underline font-medium">
                  İletişim sayfamıza git →
                </a>
              </p>
            </div>
          </section>

        </div>

        {/* Footer Note */}
        <div className="mt-8 text-center text-gray-600 text-sm">
          <p>
            Bu gizlilik politikası, Türkiye Cumhuriyeti'nin 6698 sayılı Kişisel Verilerin Korunması Kanunu (KVKK) 
            ve Avrupa Birliği Genel Veri Koruma Tüzüğü (GDPR) gerekliliklerine uygun olarak hazırlanmıştır.
          </p>
        </div>
      </div>
    </div>
  );
};

export default Privacy;





